import React, { useEffect, useState } from 'react'
import { Box, makeStyles, Chip } from '@material-ui/core'

import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  CalendarTodayOutlined,
  ForumOutlined
} from '@material-ui/icons'
import eventSlice, {
  fetchEvents,
  deleteEventById,
  ALL_DATES_FILTER,
  fetchEventTypes
} from 'store/event'
import buildingSlice, { fetchBuildings } from 'store/building'
import { renderCol } from 'utils/renderColumns'
import ResourceNotFound from 'components/ResourceNotFound'
import getTableOptions from 'utils/getTableOptions'
import Tooltip from '@material-ui/core/Tooltip'
import BasicTable from 'components/BasicTable'
import { formatTimestamp } from 'utils/times'
import Wrapper from 'components/Wrapper'
import moment from 'moment'
import { getContrastColor } from 'utils/colors'
import { toast } from 'utils/notify'
import ConfirmModal from 'components/ConfirmationModal'

const EventListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [loading, setLoading] = useState(false)
  const [deletedEvent, setDeletedEvent] = useState(null)
  const [deletingEvent, setDeletingEvent] = useState(false)
  const [formattedEvents, setFormattedEvents] = useState([])
  const [cardColors, setCardColors] = useState([])
  const [dateFilters, setDateFilters] = useState([ALL_DATES_FILTER])
  const [originalDates, setOriginalDates] = useState([]) // Store original dates without counts

  const { events, eventTypes, selectedDateFilter, currentPage, pageSize } = useSelector(
    (state) => state.event
  )
  const { user } = useSelector((state) => state.auth)
  const { buildings } = useSelector((state) => state.building)

  const [eventsWithType, setEventsWithType] = useState([])

  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    const unsubBuildings = fetchBuildings(user?.conferenceId, (buildingsData) => {
      dispatch(buildingSlice.actions.setBuildings(buildingsData))
    })
    dispatch(fetchEventTypes())

    return () => unsubBuildings && unsubBuildings()
  }, [dispatch, user?.conferenceId])

  useEffect(() => {
    const card_colors = events.reduce((prev, curr) => {
      const bgColor = eventTypes.find((color) => color.id === curr.cardStyle)
      prev[curr.id] = {
        backgroundColor: bgColor?.bgColor,
        color: getContrastColor(bgColor?.bgColor ? bgColor?.bgColor : '#FFFFFF')
      }
      return prev
    }, {})
    setCardColors(card_colors)
  }, [eventTypes, events])

  useEffect(() => {
    if (eventsWithType.length && conference) {
      const filters = []
      const data = eventsWithType.reduce((prev, cur) => {
        const date = moment(cur.startDate).tz(conference?.timezone).format('M/D/YYYY')
        if (!prev[date]) {
          filters.push(date)
          prev[date] = [cur]
        } else {
          prev[date].push(cur)
        }
        return prev
      }, {})
      data[ALL_DATES_FILTER] = eventsWithType

      if (!filters.includes(selectedDateFilter)) {
        dispatch(eventSlice.actions.setSelectedDateFilter(ALL_DATES_FILTER))
      }

      // Store original dates (without counts) for accessing the data object
      const sortedDates = [ALL_DATES_FILTER, ...filters.sort()]
      setOriginalDates(sortedDates)

      // Create date filters with counts for display
      const filtersWithCount = sortedDates.map((date) => {
        return `${date} (${date === ALL_DATES_FILTER ? eventsWithType.length : data[date].length})`
      })

      setDateFilters(filtersWithCount)
      setFormattedEvents(data)
    }
  }, [eventsWithType, conference])

  useEffect(() => {
    setEventsWithType(
      events.map((event) => ({
        ...event,
        building: buildings.find((type) => type.id === event.venueId)
      }))
    )
  }, [events, buildings])

  useEffect(() => {
    setLoading(true)
    fetchEvents(user?.conferenceId)
      .then((data) => {
        dispatch(eventSlice.actions.setEvents(data))
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
    return () => dispatch(eventSlice.actions.setEvents([]))
  }, [user?.conferenceId])

  const handleReloadEvents = async () => {
    const data = await fetchEvents(user?.conferenceId)
    dispatch(eventSlice.actions.setEvents(data))
  }

  const closeModal = () => {
    setDeletedEvent(null)
  }

  const handleDeleteEvent = async () => {
    if (deletedEvent?.id) {
      setDeletingEvent(true)
      await dispatch(deleteEventById(deletedEvent?.id))
      await handleReloadEvents()
      toast.success('Event deleted.')
    }
    setDeletingEvent(false)
  }

  const tableHead = [
    {
      name: '',
      options: {
        customBodyRender: (value) => value,
        display: false
      }
    },
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '30px' }}>{value}</div>
      }
    },
    'Title',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {formatTimestamp('M/D/YY', value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {formatTimestamp('M/D/YY', value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    'Building',
    'Has Sub Events',
    { name: 'Has Polls', options: { display: conference?.havePolls } },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const formatTableData = (events) =>
    events.map((event) => [
      event?.id,
      event?.srNo,
      event?.title,
      event.startDate,
      event.endDate,
      event?.building?.buildingText,
      event?.hasSubEvents ? 'Yes' : 'No',
      event.havePolls ? 'Yes' : 'No',
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
        {event?.hasSubEvents ? (
          <Tooltip title="View Sub Events">
            <CalendarTodayOutlined
              color="inherit"
              style={{ marginRight: 10, cursor: 'pointer' }}
              onClick={() => history.push(`${location.pathname}/events/${event.id}/sub-events`)}
            />
          </Tooltip>
        ) : (
          <>
            {conference?.enableAcademics && (
              <Tooltip title="View Talks/Posters">
                <ForumOutlined
                  color="inherit"
                  style={{ marginRight: 10, cursor: 'pointer' }}
                  onClick={() => history.push(`${location.pathname}/events/${event.id}/talks`)}
                />
              </Tooltip>
            )}
          </>
        )}
        <Tooltip title="Edit">
          <EditIcon
            color="inherit"
            style={{ marginRight: 10, cursor: 'pointer' }}
            onClick={() => history.push(`${location.pathname}/events/edit/${event.id}`)}
          />
        </Tooltip>

        <Tooltip title="Delete">
          <DeleteIcon
            color="inherit"
            style={{ marginRight: 10, cursor: 'pointer' }}
            onClick={() => setDeletedEvent(event)}></DeleteIcon>
        </Tooltip>
      </div>
    ])

  const options = getTableOptions()

  const updatedOptions = {
    ...options,
    setRowProps: (row) => {
      return {
        style: {
          backgroundColor: cardColors[row[0]]?.backgroundColor,
          color: cardColors[row[0]]?.color
        }
      }
    }
  }
  return (
    <>
      <Box className={classes.filterStatsBar}>
        <div className={classes.chipList}>
          {dateFilters?.map((filter, index) => {
            // Get the corresponding original date (without count)
            const originalDate = originalDates[index]
            return (
              <Chip
                key={filter}
                label={filter}
                clickable
                color={selectedDateFilter === originalDate ? 'primary' : 'default'}
                onClick={() => dispatch(eventSlice.actions.setSelectedDateFilter(originalDate))}
                className={classes.chip}
              />
            )
          })}
        </div>
      </Box>

      <Wrapper inProgress={loading}>
        {eventsWithType && eventsWithType.length ? (
          <BasicTable
            data={formatTableData(formattedEvents[selectedDateFilter] || [])}
            columns={tableHead}
            options={{
              ...updatedOptions,
              onChangePage: (page) => dispatch(eventSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(eventSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        ) : (
          <ResourceNotFound message="No event available" />
        )}
      </Wrapper>
      <ConfirmModal
        dialogTitle="Delete event"
        dialogText="Are you sure you want to delete this event?"
        dialogText2={`${
          deletedEvent?.hasSubEvents
            ? 'This will delete all the sub events and posters/talks if any are contained in the event.'
            : 'This will delete all the posters/talks if any are contained in the event.'
        } This action can not be reverted.`}
        open={!!deletedEvent?.id}
        closeModal={closeModal}
        handleConfirm={handleDeleteEvent}
        inProgress={deletingEvent}
      />
    </>
  )
}

export default EventListing

const useStyles = makeStyles({
  filterStatsBar: {
    marginTop: '10px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    margin: '0px 8px 16px 8px'
  },
  dateFilterDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  dateFilterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '4px',
    fontWeight: 'bold'
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4
  },
  chip: {
    fontWeight: 500,
    fontSize: '15px',
    padding: '10px 8px'
  }
})
